
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ChecklistDropdownField } from '@/interfaces/components/configurator/ChecklistDropdownField';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { Getter } from 'vuex-class';
import {
    extractAndRunOptionsFunction,
    focusDropdownElement,
    mapVisibleOptionVIDsToChecklistDropdownOptions,
} from '@/helpers/FieldFunctionHelper';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { EventBus } from '@/helpers/EventBusHelper';
import { Select } from 'ant-design-vue';
import { defaultValueWarnings as defaultValueWarningsConfig } from '@/interfaces/components/projectNew/DefaultValueWarnings';
import { GlobalOptions } from '@/enums/global/GlobalOptions';
import {
    allowedColorModuleProducts,
    allowedColorModuleProductSubGroups,
    allowedColorModuleProductGroups,
} from '@/regex/allowedColorModuleEntities';
import { checklistFieldsPIDsSet } from '@/regex/validChecklistFieldsForColorModule';

@Component({
    name: 'ChecklistDropdownFieldModule',
})
export default class ChecklistDropdownFieldModule extends Vue {
    @Prop({ required: true })
    private checklistDropdownField!: ChecklistDropdownField;
    @Prop({ required: true }) private pId!: string;
    @Prop({ default: 999 }) private itemRowIndex!: number;
    @Prop({ default: false }) private isDisabled!: boolean;
    @Prop({ required: true }) private image!: string | null;
    @Prop({ default: null }) private debounceToggleIsActive!: ((state: boolean) => void) | null;
    @Prop({ default: false }) private allowAllDropdownOptions!: boolean | null;
    @Prop({ default: null }) private activeProductFormId!: number | null;
    @Prop({ default: false }) private isConfigurator!: boolean;
    @Getter('configurator/activeProductFormValue') private productFormValue!: (
        pId: string,
        productFormId: number
    ) => string | null;
    @Prop({ required: false, default: false }) private productDetails!: any;
    @Prop({ required: false }) private checklistSegmentName!: string;

    private value = '#';
    private visibleOptionVIDs: string[] = [];
    private isOpen = false;
    private isWidthDynamicallyAdjusted = false;
    private globalOptions = GlobalOptions;
    private svgSource = require('../../../../public/color-picker.svg');
    private allowedColorModuleProducts: string[] = allowedColorModuleProducts;

    private get dropdownClassName() {
        return `c-dropdown-${this.pId}-${this.itemRowIndex}`;
    }

    private get visibleDropdownOptions() {
        if (this.visibleOptionVIDs.length <= 0) {
            return this.checklistDropdownField.checklistDropdownOptions;
        }

        return mapVisibleOptionVIDsToChecklistDropdownOptions(
            this.visibleOptionVIDs,
            this.checklistDropdownField.checklistDropdownOptions
        );
    }

    private get defaultValueWarnings() {
        const warnings = this.$store.getters['configurator/defaultValueWarnings'];

        if (warnings.length) {
            return warnings.find(
                (warning: defaultValueWarningsConfig) => warning.productFormId === this.activeProductFormId
            );
        }

        return null;
    }

    private get fieldValue() {
        let value: string | null = '';
        if (this.activeProductFormId == null) {
            this.value = value;
            return '';
        }

        value = this.productFormValue(this.pId, this.activeProductFormId);

        if (value == null) {
            this.value = '';
            return;
        }
        this.value = value;

        return value;
    }

    private get shouldShowAsButtons() {
        return this.visibleDropdownOptions.length <= 3;
    }

    private get isRoltekUserGroup(): boolean {
        return this.$store.getters['jwtData/isRoltekUserGroup'];
    }

    private get isColorModuleEnabled(): boolean {
        // Products that have limited color selection and it does not make sense to use color module
        // or we should use limited version of it:
        // Karo ( Omarica and Vodila ) - already removed
        // Žaluzija z lamelami F/Z/S ( Does not work at all, no checklist structure ) - removed
        // GECKO ( I noticed a bug when selecting "uniform system color", there is still The color of the finishing lamella and Guide color categories left visible - probably a checklist visibility function issue )
        // Checklist fields potential issues:
        // VODILA: MANUAL REGISTRATION IN SILENTO.2 and HERO.5X is hidden
        // OMARICA: NO MANUAL REGISTRATION IN VECTO
        // OMARICA SERVICE COVER: NON_STANDARD_COLOR is hidden in VISIO

        if (!checklistFieldsPIDsSet.has(this.pId)) {
            return false;
        }

        const productCategory = this.productDetails?.productCategory;
        const productName = this.productDetails?.productName;
        const productSystemName = this.productDetails?.productSystemName;

        if (productCategory && allowedColorModuleProductGroups.length > 0) {
            for (const group of allowedColorModuleProductGroups) {
                if (productCategory.toLowerCase().includes((group as string).toLowerCase())) {
                    return true;
                }
            }
        }

        // Check product subgroups if available
        if (productCategory && allowedColorModuleProductSubGroups.length > 0) {
            for (const subGroup of allowedColorModuleProductSubGroups) {
                if (productCategory.toLowerCase().includes((subGroup as string).toLowerCase())) {
                    return true;
                }
            }
        }

        // Check product names if available
        if (productName && allowedColorModuleProducts.length > 0) {
            for (const product of allowedColorModuleProducts) {
                if (productName.toLowerCase().includes((product as string).toLowerCase())) {
                    if (productName === 'KARO') {
                        // Do edge case for KARO and turn off color module if segment is omarica or vodila
                        if (this.checklistSegmentName === 'Omarica' || this.checklistSegmentName === 'Vodila') {
                            return false;
                        }
                    }
                    return true;
                }

                if (productSystemName === product) {
                    return true;
                }
            }
        }

        return false;
    }

    private get areNewCMChangesEnabled(): boolean {
        return (
            this.isColorModuleEnabled &&
            this.allowedColorModuleProducts.includes(this.productDetails?.productSystemName)
        );
    }

    private get oldCMActive(): boolean {
        return false;
    }

    public runVisibleOptionVIDs({
        activeProductFormValues,
        activeProductFormId,
    }: {
        activeProductFormValues: ActiveProductFormValueObject;
        activeProductFormId: number;
    }) {
        if (activeProductFormId !== this.activeProductFormId) {
            return;
        }

        if (
            this.checklistDropdownField.optionsFunction == null ||
            this.checklistDropdownField.optionsFunctionData == null ||
            this.allowAllDropdownOptions
        ) {
            this.visibleOptionVIDs = [];
            return;
        }
        this.visibleOptionVIDs = extractAndRunOptionsFunction(
            this.checklistDropdownField.optionsFunction,
            activeProductFormValues,
            this.checklistDropdownField.optionsFunctionData
        ).split(';');
        // Testing performance without javascript calculations
        // this.visibleOptionVIDs = [];

        if (this.fieldValue == null || this.visibleOptionVIDs.includes(this.fieldValue)) {
            return;
        }

        this.onChange(this.visibleOptionVIDs[0]);
    }

    private async onDropdownVisibleChange(state: boolean) {
        this.isOpen = state;

        if (!state) {
            this.revertDefaultWidthConfigurationForDropdownElements();

            return;
        }

        await this.$nextTick();

        this.setFlexibleWidthForDropdownElements();
    }

    private setFlexibleWidthForDropdownElements() {
        const activeDropdownEl = document.querySelector(`.${this.dropdownClassName}`) as HTMLElement;
        const viewportWidth = window.innerWidth;
        const availableSpaceToTheRight = (viewportWidth - activeDropdownEl.getBoundingClientRect().right) as number;

        if (availableSpaceToTheRight >= 0) {
            return;
        }

        const elWidth = activeDropdownEl.offsetWidth;
        const newWidth = String(elWidth + availableSpaceToTheRight) + 'px';
        activeDropdownEl.style.width = newWidth;

        activeDropdownEl.querySelectorAll('.c-dropdown__value').forEach((element: any) => {
            element.style['white-space'] = 'pre-wrap';
            element.style['word-wrap'] = 'break-word';
        });

        this.isWidthDynamicallyAdjusted = true;
    }

    private revertDefaultWidthConfigurationForDropdownElements() {
        if (!this.isWidthDynamicallyAdjusted) {
            return;
        }

        const activeDropdownEl = document.querySelector(`.${this.dropdownClassName}`) as HTMLElement;
        activeDropdownEl.style.width = 'auto';

        activeDropdownEl.querySelectorAll('.c-dropdown__value').forEach((element: any) => {
            element.style['white-space'] = 'nowrap';
            element.style['word-wrap'] = 'normal';
        });
    }

    private async onOpenDropdown(event: InputEvent) {
        const isTargetElementRoot = (event.target as HTMLElement).classList.contains('c-dropdown');

        // must check if the target element is root because otherwise it might open when user is selecting an option
        if (this.isOpen || !isTargetElementRoot) {
            return;
        }

        this.isOpen = true;

        await focusDropdownElement(event, this.$refs.dropdown as Select);
    }

    private onSelect() {
        EventBus.$emit(EventBusEvents.changesInDataMade, { state: true });
        window.setTimeout(() => {
            this.debounceToggleIsActive && this.debounceToggleIsActive(true);
        }, 200);
    }

    private onCustomDropdownSelect(value: string) {
        this.value = value;
        this.onChange(value);

        this.onSelect();
    }

    private onChange(checked: string) {
        this.$emit(EventBusEvents.updateStoreFieldValue, {
            pId: this.pId,
            value: checked,
            productFormId: this.activeProductFormId,
        });
    }

    private onBlur() {
        this.isOpen = false;
        this.debounceToggleIsActive && this.debounceToggleIsActive(false);
    }

    private onFocus() {
        window.setTimeout(() => {
            this.debounceToggleIsActive && this.debounceToggleIsActive(true);
        }, 200);
    }

    private updateColorValue(payload: {
        activeProductFormId: number;
        colorSystemPID: string;
        colorCollection: string;
        colorDropdownPID: string;
        selectedColorValue: string | null;
        shouldFreezeManualInput?: boolean;
    }) {
        if (payload.colorSystemPID !== this.pId) {
            return;
        }

        this.value = payload.colorCollection;
        this.onChange(payload.colorCollection);

        if (payload.selectedColorValue) {
            this.$nextTick(() => {
                this.$emit(EventBusEvents.updateStoreFieldValue, {
                    pId: payload.colorDropdownPID,
                    value: payload.selectedColorValue as string,
                });
            });

            EventBus.$emit(EventBusEvents.updateDisabledFieldsInMultiposition, payload);
        }
    }

    private openColorModule(payload: {
        colorSystemPID: string;
        fieldValue: GlobalOptions;
        activeProductFormId: number | null;
        productName?: string;
        productCategory?: string;
    }) {
        EventBus.$emit(EventBusEvents.showColorModule, payload);
    }

    private created() {
        // @ts-ignore
        // console.log('mountedddddddd', this._uid, 'wtf');
        EventBus.$on(EventBusEvents.triggerOptionsFunctions, this.runVisibleOptionVIDs);

        if (this.activeProductFormId === null) {
            EventBus.$on(EventBusEvents.updateMultipositionHeaderSystemColorValue, this.updateColorValue);
        }
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.triggerOptionsFunctions, this.runVisibleOptionVIDs);

        if (this.activeProductFormId === null) {
            EventBus.$off(EventBusEvents.updateMultipositionHeaderSystemColorValue, this.updateColorValue);
        }
    }

    @Watch(`fieldValue`, { immediate: true })
    private onFieldValueChange(newValue: string | null, oldValue: string | null) {
        if (newValue === oldValue) {
            return;
        }
        if (newValue == null) {
            this.value = '';
            return;
        }

        this.value = newValue;
    }
}
